// Settings > Two-step verification: the intentional enrollment view (B6). Authenticator apps (TOTP)
// are the only second factor; phone numbers (SMS) and email codes are never used. The key appears
// only after the password is confirmed (a Google-only session has no password, so it goes straight
// to the key) and only in this view; "on" is shown only after the server verified a code.
// Finishing signs the account out everywhere, so it waits until calls end.
import { type FormEvent, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { ShieldCheck } from 'lucide-react';
import { APP_NAME } from '../../config/brand.ts';
import { useWorkspace } from '../../state/workspace.tsx';
import { useSessionActions } from '../../state/sessionActions.ts';
import { useDesktop } from '../../desktop/bridge.ts';
import { formatAbsolute } from '../../lib/format.ts';
import { Icon } from '../../ui/Icon.tsx';
import { GOOGLE_ONLY_SESSION, MfaEnrollFlow, type MfaSnapshot } from '../../identity/mfa.ts';
import { AuthenticatorKey, CodeField, FieldError, FlowDiagnostics, NewCodeNotice, PasswordField } from '../../identity/IdentityParts.tsx';

export function TwoStepSection({ flow: injected }: { flow?: MfaEnrollFlow }) {
  const ws = useWorkspace();
  const desktop = useDesktop();
  const actions = useSessionActions();
  const alreadyOn = ws.session.auth_methods.includes('totp');
  // Staff signed in with Google and no legacy password have nothing to confirm before the key.
  const requiresPassword = ws.session.auth_methods.includes('password') || !ws.session.auth_methods.includes('google');
  const [flow] = useState(() => injected ?? new MfaEnrollFlow({ api: ws.api, alreadyOn, requiresPassword, onSessionProblem: () => { void actions?.reloadSession(); } }));
  const snap = useSyncExternalStore(flow.subscribe, flow.getSnapshot, flow.getSnapshot);
  const userId = ws.session.user?.id ?? null;
  const csrf = ws.session.csrf_token || null;
  useEffect(() => { flow.setSession(userId, csrf); }, [flow, userId, csrf]);
  useEffect(() => {
    if (desktop) return;
    flow.attach();
    return () => flow.detach();
  }, [flow, desktop]);
  const ended = useRef(false);
  useEffect(() => {
    if (snap.phase !== 'enrolled' || ended.current) return;
    ended.current = true;
    void actions?.requireFreshSignIn('mfa_enrolled');
  }, [snap.phase, actions]);

  return (
    <section className="settings-tile" aria-labelledby="set-2sv">
      <h2 id="set-2sv" className="section-title">Two-step verification</h2>
      <p className="field-hint">Only authenticator apps (TOTP) are allowed for two-step verification. Phone numbers (SMS) and email codes are not used.</p>
      {desktop ? (
        <p className="field-hint">Set this up in your web browser: sign in to {APP_NAME} there and open Settings. The desktop app never asks for your password.</p>
      ) : (
        <TwoStepBody snap={snap} flow={flow} liveCall={ws.liveCalls.length > 0} onSignIn={() => { void actions?.requireFreshSignIn('fresh_sign_in'); }} />
      )}
    </section>
  );
}

function TwoStepBody({ snap, flow, liveCall, onSignIn }: { snap: MfaSnapshot; flow: MfaEnrollFlow; liveCall: boolean; onSignIn: () => void }) {
  switch (snap.phase) {
    case 'idle':
    case 'loading':
      return <p className="meta" role="status">Checking…</p>;
    case 'on':
      return <p className="field-hint"><Icon icon={ShieldCheck} /> On. You sign in with {flow.requiresPassword ? 'your password' : 'Google'} and a code from your authenticator app.</p>;
    case 'unavailable':
      return (
        <>
          <p className="inline-reason">Setting up two-step verification isn’t available on this server yet.</p>
          <FlowDiagnostics diagnostic={snap.diagnostic} />
        </>
      );
    case 'off':
      return (
        <>
          <p className="field-hint">Off. Add a code from an authenticator app to your sign-in, so {flow.requiresPassword ? 'a password' : 'a Google sign-in'} alone can’t open your account.</p>
          <div className="form-actions"><button type="button" className="btn" onClick={() => flow.start()}>Set up two-step verification</button></div>
        </>
      );
    case 'password':
    case 'preparing':
      return flow.requiresPassword ? <PasswordStep snap={snap} flow={flow} /> : <KeyStep snap={snap} flow={flow} />;
    case 'enroll':
    case 'confirming':
      return <EnrollStep snap={snap} flow={flow} liveCall={liveCall} />;
    case 'enrolled':
      return (
        <div role="status">
          <NewCodeNotice />
          <p className="field-hint">Two-step verification is on. Signing you out everywhere…</p>
        </div>
      );
    case 'unknown':
      return (
        <>
          <p className="inline-reason" role="alert">We couldn’t confirm whether two-step verification turned on. Sign in again to check.</p>
          <FlowDiagnostics diagnostic={snap.diagnostic} />
          <div className="form-actions"><button type="button" className="btn btn-primary" onClick={onSignIn}>Sign in again</button></div>
        </>
      );
    case 'account_changed':
      return <p className="field-hint" role="status">Your account changed in this browser, so setup was cleared.</p>;
    default:
      return (
        <>
          <p className="inline-reason" role="alert">Couldn’t check whether two-step verification can be set up.</p>
          <FlowDiagnostics diagnostic={snap.diagnostic} />
          <div className="form-actions"><button type="button" className="btn" onClick={() => flow.retry()}>Try again</button></div>
        </>
      );
  }
}

const NOTICE = {
  step_expired: 'Setup timed out, so nothing changed. Enter your password to start again.',
  session_changed: 'Your browser session changed, so setup was cleared. Enter your password to start again.',
  too_many_codes: 'Too many codes didn’t work, so setup was cleared. Enter your password to get a new key.',
} as const;

const GOOGLE_NOTICE = {
  step_expired: 'Setup timed out, so nothing changed. Choose Continue to start again.',
  session_changed: 'Your browser session changed, so setup was cleared. Choose Continue to start again.',
  too_many_codes: 'Too many codes didn’t work, so setup was cleared. Choose Continue to get a new key.',
} as const;

/** The Google-only stand-in for the password step: shown while the key loads, and after a reset. */
function KeyStep({ snap, flow }: { snap: MfaSnapshot; flow: MfaEnrollFlow }) {
  const busy = snap.phase === 'preparing';
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!busy) void flow.prepare(GOOGLE_ONLY_SESSION);
  };
  return (
    <form className="auth-form" onSubmit={submit} noValidate aria-busy={busy || undefined}>
      {snap.notice ? <p className="notice notice-warn" role="status">{GOOGLE_NOTICE[snap.notice]}</p> : null}
      <p className="field-hint">You signed in with Google, so there’s no password to confirm. Your setup key comes next.</p>
      <FieldError id="mfa-key-error" message={snap.fieldError} />
      <div className="form-actions">
        <button type="button" className="btn" onClick={() => flow.abandon()}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>{busy ? 'Getting your key…' : 'Continue'}</button>
      </div>
    </form>
  );
}

function PasswordStep({ snap, flow }: { snap: MfaSnapshot; flow: MfaEnrollFlow }) {
  const [password, setPassword] = useState('');
  const busy = snap.phase === 'preparing';
  useEffect(() => () => setPassword(''), []);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const value = password;
    setPassword('');
    void flow.prepare(value);
  };
  return (
    <form className="auth-form" onSubmit={submit} noValidate aria-busy={busy || undefined}>
      {snap.notice ? <p className="notice notice-warn" role="status">{NOTICE[snap.notice]}</p> : null}
      <PasswordField id="mfa-password" label="Your current password" value={password} onChange={setPassword} autoComplete="current-password" hint="Confirm it’s you before the setup key is shown." autoFocus />
      <FieldError id="mfa-password-error" message={snap.fieldError} />
      <div className="form-actions">
        <button type="button" className="btn" onClick={() => flow.abandon()}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>{busy ? 'Checking…' : 'Continue'}</button>
      </div>
    </form>
  );
}

function EnrollStep({ snap, flow, liveCall }: { snap: MfaSnapshot; flow: MfaEnrollFlow; liveCall: boolean }) {
  const [code, setCode] = useState('');
  const busy = snap.phase === 'confirming';
  useEffect(() => () => setCode(''), []);
  if (!snap.mfa) return null;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy || liveCall) return;
    const value = code;
    setCode('');
    void flow.confirm(value);
  };
  return (
    <form className="auth-form" onSubmit={submit} noValidate aria-busy={busy || undefined}>
      <AuthenticatorKey mfa={snap.mfa} />
      <CodeField id="mfa-code" value={code} onChange={setCode} error={!!snap.fieldError} autoFocus />
      {snap.stepExpiresAt ? <p className="meta">This setup stays open until {formatAbsolute(snap.stepExpiresAt)}.</p> : null}
      <p className="meta">Turning it on signs you out everywhere, including this browser’s phone. You’ll sign in again with {flow.requiresPassword ? 'your password' : 'Google'} and the next code your app shows. Each code works only once.</p>
      {liveCall ? <p className="inline-reason" id="mfa-why">Finish your call first: turning this on ends every session, including this call.</p> : null}
      <FieldError id="mfa-code-error" message={snap.fieldError} />
      <div className="form-actions">
        <button type="button" className="btn" onClick={() => flow.abandon()} disabled={busy}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={busy || liveCall} aria-describedby={liveCall ? 'mfa-why' : undefined} aria-busy={busy || undefined}>{busy ? 'Checking…' : 'Turn on'}</button>
      </div>
    </form>
  );
}
